import {
  FileSpreadsheet,
  BarChart3,
  Calculator,
  AlertTriangle,
  TrendingUp,
  Scale,
  DollarSign,
  CalendarClock,
  Gauge,
  Package2,
  Users,
  LineChart,
  MessageSquare,
} from 'lucide-react';
import { Tool, ToolCategory } from '@/types/tools';

// Alphabetized TOOL_CATEGORIES (unchanged)
export const TOOL_CATEGORIES: ToolCategory[] = [
  {
    id: 'calculators',
    name: 'Calculators',
    description: 'Tools for managing project costs, budgets, and financial analysis.',
  },
  {
    id: 'contract-management',
    name: 'Contract Management',
    description:
      'Tools to manage contracts, early warnings, and compensation events.',
  },
  {
    id: 'forecasting',
    name: 'Forecasting',
    description:
      'Tools for project planning, scheduling, and resource management.',
  },
  {
    id: 'reporting',
    name: 'Reporting and Insights',
    description: 'Tools for generating reports and analyzing project data.',
  },
  {
    id: 'tracking',
    name: 'Tracking and Management',
    description:
      'Tools for monitoring and managing project resources and performance.',
  },
];

// Alphabetized TOOLS (grouped by category, then sorted alphabetically by title)
export const TOOLS: Tool[] = [
  // Calculators
  {
    id: 'evm',
    title: 'Earned Value Management',
    description:
      'Automate project performance tracking by entering planned, earned, and actual values to generate key EVM metrics, professional reports, and visuals.',
    icon: TrendingUp,
    path: '/evm',
    color: 'text-green-500',
    category: 'calculators',
  },
  {
    id: 'impact-analysis',
    title: 'Impact Analysis',
    description:
      'Assess cost and schedule impacts of changes to improve risk control and decision-making with clear visuals and reports.',
    icon: Calculator,
    path: '/impact',
    color: 'text-red-500',
    category: 'calculators',
    comingSoon: true,
  },
  {
    id: 'cvr',
    title: 'Project Cost Value Reconciliation',
    description:
      'Input project costs, values, and forecasts to automatically generate professional PDF reports and graphs, streamlining your CVR process.',
    icon: FileSpreadsheet,
    path: '/cvr',
    color: 'text-blue-500',
    category: 'calculators',
  },
  {
    id: 'risk-assessment',
    title: 'Risk Assessment',
    description:
      'Identify, prioritise, and mitigate project risks for better planning, improved control, and smarter decision-making.',
    icon: AlertTriangle,
    path: '/risk',
    color: 'text-yellow-500',
    category: 'calculators',
    comingSoon: true,
  },
  {
    id: 'target-cost',
    title: 'Target Cost Pain/Gain Share',
    description:
      'Simplify calculations for target cost agreements, pain/gain share splits, and project outcomes with clear metrics, reports, and visuals.',
    icon: Scale,
    path: '/target-cost',
    color: 'text-purple-500',
    category: 'calculators',
  },

  // Contract Management
  {
    id: 'compensation-event-quotation',
    title: 'Compensation Event Quotation',
    description:
      'Prepare clear and professional compensation event quotations.',
    icon: DollarSign,
    path: '/coming-soon',
    color: 'text-green-500',
    category: 'contract-management',
    comingSoon: true,
  },
  {
    id: 'compensation-event-register',
    title: 'Compensation Event Register ',
    description:
      'Streamline compensation event submissions with prebuilt templates.',
    icon: FileSpreadsheet,
    path: '/coming-soon',
    color: 'text-blue-500',
    category: 'contract-management',
    comingSoon: true,
  },
  {
    id: 'early-warning-register',
    title: 'Early Warning Register',
    description: 'Track and manage early warning notices with a clear, structured approach.',
    icon: AlertTriangle,
    path: '/early-warning-register',
    color: 'text-yellow-500',
    category: 'contract-management',
    comingSoon: false,
  },


  // Forecasting
  {
    id: 'cash-flow',
    title: 'Cash Flow Forecasting',
    description:
      'Predict cash inflows and outflows to maintain financial stability and resource planning.',
    icon: DollarSign,
    path: '/cash-flow',
    color: 'text-emerald-500',
    category: 'forecasting',
    comingSoon: true,
  },
  {
    id: 'schedule',
    title: 'Schedule Forecasting',
    description:
      'Forecast project timelines and adjust schedules using real-time data and insights.',
    icon: CalendarClock,
    path: '/schedule',
    color: 'text-orange-500',
    category: 'forecasting',
    comingSoon: true,
  },

  // Reporting and Insights
  {
    id: 'performance',
    title: 'Performance Insights',
    description:
      'Visualise project metrics and trends for informed decision-making and performance reviews.',
    icon: LineChart,
    path: '/performance',
    color: 'text-teal-500',
    category: 'reporting',
    comingSoon: true,
  },
  {
    id: 'stakeholder',
    title: 'Stakeholder Reporting',
    description:
      'Generate actionable, visually appealing reports tailored to stakeholder requirements.',
    icon: Users,
    path: '/stakeholder',
    color: 'text-rose-500',
    category: 'reporting',
    comingSoon: true,
  },
  {
    id: 'whatsapp-analyzer',
    title: 'WhatsApp Project Chat Analyzer',
    description:
      'Analyze project communication patterns, key topics, and team engagement from WhatsApp chat exports.',
    icon: MessageSquare,
    path: '/whatsapp-analyzer',
    color: 'text-green-500',
    category: 'reporting',
  },

  // Tracking and Management
  {
    id: 'materials',
    title: 'Materials Tracking',
    description:
      'Manage material orders and deliveries aligned with project schedules.',
    icon: Package2,
    path: '/materials',
    color: 'text-indigo-500',
    category: 'tracking',
    comingSoon: true,
  },
  {
    id: 'productivity',
    title: 'Productivity Tracker',
    description:
      'Monitor site productivity by comparing planned vs actual performance metrics.',
    icon: Gauge,
    path: '/productivity',
    color: 'text-cyan-500',
    category: 'tracking',
    comingSoon: true,
  },
];
